import React, { useEffect, useState, useCallback, useRef } from 'react';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';
import KwPanel from '../../common/KwPanel';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as steamApi from '../../../api/steamApi';
import * as gameApi from '../../../api/gameApi';
import * as skuApi from '../../../api/skuApi';
import * as packageTypeApi from '../../../api/packageTypeApi';
import { toast } from 'react-toastify';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { fetchCompanies } from '../../../reducers/companyReducer';
import { getCompanyIdsForPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import SteamGameImportHeader from '../components/steamImport/SteamGameImportHeader';
import SteamGameImportSetupForm from '../components/steamImport/SteamGameImportSetupForm';
import SteamGameImportRunView from '../components/steamImport/SteamGameImportRunView';
import SteamGameImportSelectionView from '../components/steamImport/SteamGameImportSelectionView';
import type { PlatformModel } from '../../../types/api';
import {
  IMPORT_STEP_DEFINITIONS,
  STEP_LABEL_KEYS,
  type FetchedGame,
  type ImportedGame,
  type KeyProviderOption,
  type LogEntry,
  type SelectableGame,
  type StepRunState,
  type SteamImportFormValues,
} from '../components/steamImport/steamGameImportTypes';

type SteamGameImportProps = {
  done: () => void;
  cancel: () => void;
};

function SteamGameImport({ done: _done, cancel }: SteamGameImportProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { register, getValues, setValue, watch } =
    useForm<SteamImportFormValues>({
      defaultValues: {
        appIds: '',
        platformId: '',
        keyProviderId: '',
        packageTypeId: '',
        publisherCompanyId: '',
      },
    });

  const platforms = useAppSelector((state) => state.platforms ?? []) as PlatformModel[];
  const companies = useAppSelector((state) => state.companies) as
    | { id: string; companyName: string; isPublisher?: boolean }[]
    | null;
  const currentUser = useAppSelector((state) => state.auth);
  const [platformKeyProvidersMap, setPlatformKeyProvidersMap] = useState<
    Record<string, KeyProviderOption[]>
  >({});
  const [packageTypes, setPackageTypes] = useState<
    { id: string; name: string }[]
  >([]);
  const [singlePlatformKeyProvider, setSinglePlatformKeyProvider] =
    useState(false);

  const steamPlatforms = platforms.filter((p) =>
    (platformKeyProvidersMap[p.id] ?? []).some(
      (k) => k.steamIntegrationEnabled === true
    )
  );
  const watchedPlatformId = watch('platformId');
  const steamKeyProvidersForSelectedPlatform =
    watchedPlatformId && platformKeyProvidersMap[watchedPlatformId]
      ? platformKeyProvidersMap[watchedPlatformId].filter(
          (k) => k.steamIntegrationEnabled === true
        )
      : [];

  const allowedPublishers = getCompanyIdsForPermission(
    currentUser,
    permissionTypes.MANAGE_GAMES
  ) as string[] | undefined;
  const publisherOptions =
    companies && allowedPublishers
      ? [...companies]
          .filter(
            (c) => c.isPublisher && allowedPublishers.some((id) => id === c.id)
          )
          .sort((a, b) =>
            (a.companyName ?? '').localeCompare(b.companyName ?? '')
          )
      : [];
  const [isImporting, setIsImporting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [importPhase, setImportPhase] = useState<'setup' | 'review' | 'run'>(
    'setup'
  );
  const [progress, setProgress] = useState(0);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [steps, setSteps] = useState<StepRunState[]>(() =>
    IMPORT_STEP_DEFINITIONS.map((s) => ({
      id: s.id,
      status: 'pending',
    }))
  );
  const [fetchedGames, setFetchedGames] = useState<FetchedGame[]>([]);
  const [selectableGames, setSelectableGames] = useState<SelectableGame[]>([]);
  const [importedGames, setImportedGames] = useState<ImportedGame[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState({ games: 0, skus: 0, timeSaved: 0 });

  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    packageTypeApi
      .getAllPackageTypes()
      .then((list) => {
        setPackageTypes(list.map((p) => ({ id: p.id, name: p.name ?? p.id })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!platforms?.length) return;
    const map: Record<string, KeyProviderOption[]> = {};
    for (const p of platforms) {
      const list = p.keyProviders ?? [];
      map[p.id] = list.map((k) => ({
        id: k.id,
        name: k.name ?? '',
        steamIntegrationEnabled: k.steamIntegrationEnabled === true,
      }));
    }
    setPlatformKeyProvidersMap(map);
  }, [platforms]);

  useEffect(() => {
    if (
      Object.keys(platformKeyProvidersMap).length === 0 ||
      platforms.length === 0
    )
      return;
    const withSteam = platforms.filter((p) =>
      (platformKeyProvidersMap[p.id] ?? []).some(
        (k) => k.steamIntegrationEnabled === true
      )
    );
    const onlyPlatform = withSteam[0];
    if (withSteam.length !== 1 || !onlyPlatform) return;
    const steamKps = (platformKeyProvidersMap[onlyPlatform.id] ?? []).filter(
      (k) => k.steamIntegrationEnabled === true
    );
    const onlyKeyProvider = steamKps[0];
    if (steamKps.length === 1 && onlyKeyProvider) {
      setValue('platformId', onlyPlatform.id);
      setValue('keyProviderId', onlyKeyProvider.id);
      setSinglePlatformKeyProvider(true);
    }
  }, [platformKeyProvidersMap, platforms, setValue]);

  useEffect(() => {
    if (!companies) {
      dispatch(fetchCompanies());
    }
  }, [companies, dispatch]);

  const addLog = useCallback(
    (message: string, type: LogEntry['type'] = 'info') => {
      const entry: LogEntry = {
        id: Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toLocaleTimeString([], {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
        message,
        type,
      };
      setLogs((prev) => [...prev.slice(-49), entry]);
    },
    []
  );

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const startImport = async () => {
    const {
      appIds,
      platformId,
      keyProviderId,
      packageTypeId,
      publisherCompanyId,
    } = getValues();
    const raw = appIds.trim().replace(/\s+/g, '');
    if (!raw) {
      toast.error(t('Enter at least one Steam App Id (comma-separated).'));
      return;
    }
    if (!platformId || !keyProviderId || !packageTypeId) {
      toast.error(t('Please select Platform, Key provider, and Package type.'));
      return;
    }
    try {
      const games = await steamApi.getGames(raw);
      const [existingGamesResponse, existingSkusResponse] = await Promise.all([
        gameApi.getAllGames(),
        skuApi.getAllSKUs(),
      ]);
      const existingGames = Array.isArray(existingGamesResponse)
        ? existingGamesResponse
        : Array.isArray((existingGamesResponse as { data?: unknown[] })?.data)
          ? ((existingGamesResponse as { data: unknown[] }).data as {
              appId?: string;
              id: string;
            }[])
          : [];
      const existingSkus = Array.isArray(existingSkusResponse)
        ? existingSkusResponse
        : Array.isArray((existingSkusResponse as { data?: unknown[] })?.data)
          ? ((existingSkusResponse as { data: unknown[] }).data as {
              appId?: string;
              packageId?: string;
            }[])
          : [];

      const existingGamesByAppId = new Map(
        existingGames
          .filter((g) => (g.appId ?? '').trim() !== '')
          .map((g) => [String(g.appId).trim(), g.id])
      );
      const existingSkuKeys = new Set(
        existingSkus
          .filter((s) => (s.appId ?? '').trim() !== '' && (s.packageId ?? '').trim() !== '')
          .map((s) => `${String(s.appId).trim()}|${String(s.packageId).trim()}`)
      );

      const normalized: FetchedGame[] = games.map((g) => ({
        name: g.title,
        appId: String(g.appId),
        skuCount: g.packages?.length ?? 0,
      }));
      const prepared: SelectableGame[] = games.map((g) => {
        const appId = String(g.appId).trim();
        const existingGameId = existingGamesByAppId.get(appId);
        const packages = (g.packages ?? []).map((pkg) => {
          const packageId = String(pkg.packageId).trim();
          const existsInAlaska = existingSkuKeys.has(`${appId}|${packageId}`);
          return {
            packageId,
            name: pkg.name,
            existsInAlaska,
            selected: !existsInAlaska,
          };
        });
        return {
          name: g.title,
          appId,
          existsInAlaska: Boolean(existingGameId),
          existingGameId,
          selected: !existingGameId || packages.some((p) => p.selected),
          packages,
        };
      });

      setFetchedGames(normalized);
      setSelectableGames(prepared);
      setImportPhase('review');
    } catch (err) {
      const rawErr =
        err instanceof Error ? err.message : typeof err === 'string' ? err : '';
      const message =
        rawErr && rawErr.trim() ? rawErr : t('Failed to fetch Steam games.');
      toast.error(message);
      addLog(message, 'warning');
      setIsImporting(false);
    }
  };

  const executeImport = async () => {
    const { platformId, keyProviderId, packageTypeId, publisherCompanyId } = getValues();
    if (!platformId || !keyProviderId || !packageTypeId) {
      toast.error(t('Please select Platform, Key provider, and Package type.'));
      return;
    }

    const selectedGames = selectableGames.filter((g) => g.selected);
    if (selectedGames.length === 0) {
      toast.error(t('Select at least one game or SKU to import.'));
      return;
    }

    setImportPhase('run');
    setIsImporting(true);
    setIsComplete(false);
    setProgress(0);
    setCurrentStepIdx(0);
    setImportedGames([]);
    setLogs([]);
    setStats({ games: 0, skus: 0, timeSaved: 0 });
    setSteps(
      IMPORT_STEP_DEFINITIONS.map((s) => ({
        id: s.id,
        status: 'pending',
      }))
    );

    addLog(t('ALASKA Engine initializing...'), 'info');
    addLog(t('Starting selected import...'), 'info');
    setSteps((s) =>
      s.map((step, i) => ({
        ...step,
        status: i < 3 ? 'complete' : i === 3 ? 'active' : 'pending',
      }))
    );
    setCurrentStepIdx(3);

    try {
      for (let i = 0; i < selectedGames.length; i++) {
        const game = selectedGames[i]!;
        const selectedPackages = game.packages.filter((p) => p.selected);
        addLog(t('Importing: {{title}}', { title: game.name }), 'info');

        try {
          const targetGameId =
            game.existingGameId ??
            (
              await gameApi.saveGame({
                name: game.name,
                appId: game.appId,
                licenseExpires: '',
                notesHTML: '',
                ...(publisherCompanyId ? { publisherCompanyId } : {}),
              })
            ).id;

          for (const pkg of selectedPackages) {
            await skuApi.saveSku(targetGameId, {
              name: pkg.name,
              packageId: pkg.packageId,
              platformId,
              keyProviderId,
              packageTypeId,
              singleKeyValue: 1,
              appId: game.appId,
            });
          }

          setImportedGames((prev) => [
            ...prev,
            {
              id: targetGameId,
              name: game.name,
              appId: game.appId,
              skuCount: selectedPackages.length,
              status: 'complete',
            },
          ]);
          setStats((prev) => ({
            games: prev.games + (game.existingGameId ? 0 : 1),
            skus: prev.skus + selectedPackages.length,
            timeSaved: prev.timeSaved + selectedPackages.length * 3,
          }));
          setProgress(((i + 1) / selectedGames.length) * 100);
          addLog(
            t('Imported {{title}} with {{skuCount}} SKU(s)', {
              title: game.name,
              skuCount: selectedPackages.length,
            }),
            'success'
          );
        } catch (gameErr) {
          const msg =
            gameErr instanceof Error ? gameErr.message : t('Failed to create game or SKUs');
          addLog(
            t('Failed to import {{title}}: {{message}}', {
              title: game.name,
              message: msg,
            }),
            'warning'
          );
          toast.error(`${game.name}: ${msg}`);
        }
      }

      setSteps((s) => s.map((step) => ({ ...step, status: 'complete' as const })));
      setCurrentStepIdx(IMPORT_STEP_DEFINITIONS.length - 1);
      setProgress(100);
      setIsComplete(true);
      setIsImporting(false);
      addLog(t('IMPORT SEQUENCE FINALIZED'), 'success');
    } catch (err) {
      const rawErr = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
      const message = rawErr && rawErr.trim() ? rawErr : t('Import failed.');
      toast.error(message);
      addLog(message, 'warning');
      setIsImporting(false);
    }
  };

  const toggleGameSelection = (appId: string) => {
    setSelectableGames((prev) =>
      prev.map((game) =>
        game.appId !== appId
          ? game
          : {
              ...game,
              selected: !game.selected,
              packages: game.packages.map((pkg) => ({
                ...pkg,
                selected: !game.selected && !pkg.existsInAlaska,
              })),
            }
      )
    );
  };

  const togglePackageSelection = (appId: string, packageId: string) => {
    setSelectableGames((prev) =>
      prev.map((game) => {
        if (game.appId !== appId) return game;
        const packages = game.packages.map((pkg) =>
          pkg.packageId === packageId ? { ...pkg, selected: !pkg.selected } : pkg
        );
        return {
          ...game,
          packages,
          selected: packages.some((pkg) => pkg.selected),
        };
      })
    );
  };

  const currentStepId = steps[currentStepIdx]?.id;
  const currentOperationLabel = currentStepId
    ? t(STEP_LABEL_KEYS[currentStepId] ?? '')
    : '';

  return (
    <SlideoutLayout
      title={t('Steam Game Import')}
      cancel={cancel}
      contentFillHeight
    >
      <KwPanel>
        <div
          className="container-fluid p-0 position-relative overflow-hidden flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <SteamGameImportHeader />

          {importPhase === 'setup' ? (
            <SteamGameImportSetupForm
              register={register}
              setValue={setValue}
              watch={watch}
              singlePlatformKeyProvider={singlePlatformKeyProvider}
              steamPlatforms={steamPlatforms}
              steamKeyProvidersForSelectedPlatform={
                steamKeyProvidersForSelectedPlatform
              }
              packageTypes={packageTypes}
              publisherOptions={publisherOptions}
              watchedPlatformId={watchedPlatformId}
              onStartImport={startImport}
            />
          ) : importPhase === 'review' ? (
            <SteamGameImportSelectionView
              games={selectableGames}
              onToggleGame={toggleGameSelection}
              onTogglePackage={togglePackageSelection}
              onBack={() => setImportPhase('setup')}
              onStartImport={executeImport}
            />
          ) : (
            <SteamGameImportRunView
              isComplete={isComplete}
              isImporting={isImporting}
              progress={progress}
              steps={steps}
              fetchedGames={fetchedGames}
              importedGames={importedGames}
              currentOperationLabel={currentOperationLabel}
              stats={stats}
              logs={logs}
              logContainerRef={logContainerRef}
              onClearLogs={() => setLogs([])}
            />
          )}
        </div>
      </KwPanel>
    </SlideoutLayout>
  );
}

export default SteamGameImport;
